import { cognitoConfig } from "../cognito/config";
import { clearStaleSessionKeys } from "./ssoCallbackHelpers";
import { MOBILE_SSO_CODE_VERIFIER_KEY } from "./startSsoRedirect";

const COGNITO_STORAGE_PREFIX = "CognitoIdentityServiceProvider";

interface CognitoTokenResponse {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
}

/**
 * Best-effort check that valid Cognito tokens are already stored. Used by
 * `RipplingSsoCallback` to defend against a remount mid-flow: if a parent
 * re-render tears the callback down between `exchangeCodeOnCapacitor` and
 * `history.replace(target)`, the second mount sees an empty URL and would
 * otherwise call `cleanupSsoState()`, wiping the session we just stored.
 */
export function hasStoredCognitoSession(): boolean {
  for (let index = 0; index < localStorage.length; index++) {
    const key = localStorage.key(index);
    if (key?.startsWith(`${COGNITO_STORAGE_PREFIX}.`) && key.endsWith(".LastAuthUser")) {
      return Boolean(localStorage.getItem(key));
    }
  }
  return false;
}

/**
 * Clears all SSO-related state from localStorage so the user can retry
 * from a clean login screen after a failure.
 */
export function cleanupSsoState(): void {
  const keysToRemove: string[] = [];
  for (let index = 0; index < localStorage.length; index++) {
    const key = localStorage.key(index);
    if (
      key?.startsWith(COGNITO_STORAGE_PREFIX) ||
      key?.includes("oauthState") ||
      key?.includes("oauthPKCE")
    ) {
      keysToRemove.push(key);
    }
  }
  for (const key of keysToRemove) {
    localStorage.removeItem(key);
  }

  localStorage.removeItem(MOBILE_SSO_CODE_VERIFIER_KEY);
  clearStaleSessionKeys();
}

/**
 * On Capacitor, Amplify's enable-oauth-listener can't process the callback
 * (the deep link arrives via SPA navigation, not a page load matching the
 * configured redirectSignIn origin). Exchange the auth code for tokens
 * manually by calling Cognito's /oauth2/token endpoint.
 */
export async function exchangeCodeOnCapacitor(authCode: string): Promise<void> {
  const codeVerifier = localStorage.getItem(MOBILE_SSO_CODE_VERIFIER_KEY);
  if (!codeVerifier) {
    throw new Error("PKCE code verifier not found in localStorage");
  }

  const { APP_CUSTOM_SCHEME } = await import("@src/mobile/utils/deepLinkHandler");
  const { SSO_CALLBACK_PATH } = await import("./paths");
  const redirectUri = `${APP_CUSTOM_SCHEME}://${SSO_CALLBACK_PATH}`;

  const tokenResponse = await fetch(`https://${cognitoConfig.oauthDomain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      code: authCode,
      redirect_uri: redirectUri,
      client_id: cognitoConfig.clientId,
      code_verifier: codeVerifier,
    }),
  });

  if (!tokenResponse.ok) {
    const body = await tokenResponse.text();
    throw new Error(`Token exchange failed: ${tokenResponse.status} ${body}`);
  }

  const raw = await tokenResponse.json();
  const tokens: CognitoTokenResponse = {
    idToken: raw.id_token,
    accessToken: raw.access_token,
    refreshToken: raw.refresh_token,
  };

  storeCognitoTokens(tokens);
  localStorage.removeItem(MOBILE_SSO_CODE_VERIFIER_KEY);
}

/**
 * Stores Cognito OAuth tokens in the localStorage format that Amplify v6
 * expects, so fetchAuthSession() can read them.
 */
function storeCognitoTokens(tokens: CognitoTokenResponse): void {
  const base64Url = tokens.idToken.split(".")[1];
  const base64 = base64Url
    .replace(/-/g, "+")
    .replace(/_/g, "/")
    .padEnd(base64Url.length + ((4 - (base64Url.length % 4)) % 4), "=");
  const payload = JSON.parse(atob(base64));
  const username = payload.sub as string;
  const prefix = `${COGNITO_STORAGE_PREFIX}.${cognitoConfig.clientId}`;

  localStorage.setItem(`${prefix}.${username}.idToken`, tokens.idToken);
  localStorage.setItem(`${prefix}.${username}.accessToken`, tokens.accessToken);
  if (tokens.refreshToken) {
    localStorage.setItem(`${prefix}.${username}.refreshToken`, tokens.refreshToken);
  }
  localStorage.setItem(`${prefix}.${username}.clockDrift`, "0");
  localStorage.setItem(`${prefix}.LastAuthUser`, username);
}
